import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { FreudConfirmationService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ICalendarEvent } from 'app/interfaces/event.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { ChatService } from 'app/services/chat.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { NotificationsService } from 'app/services/notifications.service';
import { PromptuarySidebarService } from 'modules/promptuary/services/sidebar/promptuary-sidebar.service';

@Component({
	selector: 'appointment-details-footer',
	templateUrl: './appointment-details-footer.component.html',
})
export class AppointmentDetailsFooterComponent {
	@Input()
	public event: ICalendarEvent;

	@Input()
	public isLoading: boolean;

	@Input()
	public hasContactId: boolean;

	@Input()
	public hasRefUserId: boolean;

	@Output()
	public rescheduleEvent: EventEmitter<void> = new EventEmitter();

	@Output()
	public refreshEvents: EventEmitter<any> = new EventEmitter<any>();

	@Output()
	public showEvents: EventEmitter<void> = new EventEmitter();

	@Output()
	public isLoadingEvent: EventEmitter<ISidebar> = new EventEmitter<ISidebar>();

	@Output()
	public scheduleNewSessionEvent: EventEmitter<void> = new EventEmitter();

	isCancelRecurrencyAppointmentModalOpen = false;

	readonly promptuaryFeatureReleaseDate = new Date('2023-01-01');

	constructor(
		public router: Router,
		public dateTimeService: DateTimeService,
		public chatService: ChatService,
		public eventService: EventsService,
		public translateService: TranslateService,
		public confirmationService: FreudConfirmationService,
		public notificationService: NotificationsService,
		public promptuarySidebarService: PromptuarySidebarService,
		public actionTrackerService: ActionTrackerService
	) {}

	public startSession(): void {
		if (this.event.status !== 'paid') {
			return;
		}

		this.router.navigate([`session/${this.event.id}`]);
	}

	public shouldDisableStartSessionButton(): boolean {
		if (this.event.status !== 'paid') {
			return true;
		}

		return false;
	}

	public shouldShowStartSessionButton(): boolean {
		if (!this.dateTimeService.dateTimeIsAfter(this.event.dayEnd)) {
			return false;
		}

		if (this.event.professionalAddress) {
			return false;
		}

		return true;
	}

	public shouldShowChatButton(): boolean {
		if (
			this.event.status === 'reserved' ||
			!this.hasContactId ||
			!this.hasRefUserId
		) {
			return false;
		}

		return !!this.event.profileId;
	}

	public async showChat(): Promise<void> {
		this.showEvents.emit();
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Details,
			ITEM_TYPE.BtnChat,
			INTERACTION_TYPE.Click,
			{}
		);
		await this.chatService.startConversation(
			{
				id: this.event.profileId,
				photoUrl: this.event.userImageUrl,
				username: this.event.userName,
				role: this.chatService.role,
			},
			'appointment-modal'
		);
	}

	public emitRescheduleEvent(): void {
		this.rescheduleEvent.emit();
	}

	public shouldShowRescheduleButton(): boolean {
		return this.eventService.allowReschedule(this.event);
	}

	public shouldShowCancelInviteButton(): boolean {
		return this.event.status == 'invite';
	}

	public cancelInvite(): void {
		if (this.event.recurringInviteScheduleId) {
			this.isCancelRecurrencyAppointmentModalOpen = true;
			return;
		}
		this.confirmationService.confirm({
			message: this.translateService.instant(
				'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.MESSAGE'
			),
			header: this.translateService.instant(
				'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.TITLE'
			),
			acceptLabel: this.translateService.instant(
				'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.ACCEPT_LABEL'
			),
			rejectLabel: this.translateService.instant(
				'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.REJECT_LABEL'
			),
			accept: () => {
				this.isLoadingEvent.emit();
				this.eventService.cancelInvite(this.event.id).subscribe(
					() => {
						this.actionTrackerService.eventTracker(
							SCOPE_TYPE.Session,
							SUBSCOPE_TYPE.Invite,
							ITEM_TYPE.Cancel,
							INTERACTION_TYPE.Click,
							{
								type: this.event.professionalAddress
									? 'face-to-face'
									: 'online',
							}
						);
						this.notificationService.showSuccess(
							this.translateService.instant(
								'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.CONFIRMATION'
							)
						);
						this.showEvents.emit();
					},
					(error) => {
						this.notificationService.showError(
							error,
							this.translateService.instant(
								'APPOINTMENTS.DETAILS.FOOTER.CANCEL_MODAL.ERROR'
							)
						);
					}
				);
			},
		});
	}

	public shouldShowScheduleNewSessionButton(): boolean {
		return (
			this.event.status === 'paid' &&
			this.dateTimeService.dateTimeIsBefore(this.event.dayEnd)
		);
	}

	public scheduleNewSession(): void {
		this.scheduleNewSessionEvent.emit();
	}

	public shouldShowPromptuaryButton(): boolean {
		if (this.event.status !== 'paid') {
			return false;
		}

		if (
			this.dateTimeService.dateTimeIsBefore(this.promptuaryFeatureReleaseDate)
		) {
			return false;
		}

		if (this.dateTimeService.dateTimeIsAfter(this.event.dayEnd)) {
			return false;
		}

		return true;
	}

	public getPromptuaryButtonLabel(): string {
		if (this.event.hasPromptuary) {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.FOOTER.VIEW_PROMPTUARY'
			);
		}

		return this.translateService.instant(
			'APPOINTMENTS.DETAILS.FOOTER.REGISTER_PROMPTUARY'
		);
	}

	public goToPromptuary(): void {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Caledar,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.BtnPromptuary,
			INTERACTION_TYPE.Click,
			{}
		);

		if (this.event.hasPromptuary) {
			this.router.navigateByUrl(
				`/promptuary/${this.event.id}/${this.event.profileId}`,
				{
					state: {
						from: 'appointment-modal',
					},
				}
			);
			return;
		}

		this.promptuarySidebarService.openSidebarOrGoToPage(
			this.event.profileId,
			this.event.id,
			'appointment-modal'
		);
		this.showEvents.emit();
	}
}
